import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { useAuth } from "@/store/auth";

const baseURL = (import.meta.env.VITE_API_BASE_URL as string) || "";

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------------- Request: bearer, branch, correlation id ----------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, user, activeBranchId } = useAuth.getState();

  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;

  // The API validates this against the user's granted branches and ignores anything unlisted, so
  // sending it is a request rather than an assertion.
  const effectiveBranchId = activeBranchId ?? user?.branchId;
  if (effectiveBranchId) config.headers["X-Branch-Id"] = effectiveBranchId;

  // One id per request, echoed back on the response and written to every audit row it produces —
  // which is what turns "it failed this morning" into a log search.
  if (!config.headers["X-Correlation-Id"]) {
    config.headers["X-Correlation-Id"] = crypto.randomUUID();
  }

  return config;
});

/** The RFC-7807 body the API returns for every failure. */
export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
  instance?: string;
  /** Present on FEATURE_NOT_ENABLED. */
  feature?: string;
  /** Present on MASTER_OWNED_EXTERNALLY. */
  master?: string;
  /** Present on ROSTER_CONFLICT. */
  conflicts?: string[];
}

// ---------------- Response: refresh once, then surrender ----------------

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchanges the refresh token for a new pair.
 *
 * <p>Shared across concurrent 401s: a screen that fires four queries at once would otherwise send
 * four refreshes, and since the API <b>rotates</b> the token on use, three of them would present an
 * already-revoked token and log the user out. One in-flight promise, awaited by all.</p>
 */
async function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    const { refreshToken, setSession, clear } = useAuth.getState();
    if (!refreshToken) return null;

    try {
      // A bare axios call, not `api` — going through the instance would re-enter this interceptor.
      const { data } = await axios.post(`${baseURL}/api/v1/auth/refresh`, { refreshToken });
      setSession(data);
      return data.accessToken as string;
    } catch {
      clear();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiProblem>) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    // Retry once, and only once. `_retried` stops a refresh that itself 401s from looping.
    if (status === 401 && original && !original._retried && !original.url?.includes("/auth/")) {
      original._retried = true;

      const token = await refreshAccessToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
    }

    if (status === 401) {
      const auth = useAuth.getState();
      const wasAuthenticated = !!auth.accessToken;
      auth.clear();

      // No location.assign: AuthGuard re-renders to /login when the session clears. A hard
      // navigation would lose the in-page state for nothing.
      if (wasAuthenticated && !location.pathname.startsWith("/login")) {
        auth.setSessionExpired(true);
      }
    }

    return Promise.reject(error);
  },
);

/** The most useful sentence available for a failure, in the order a user would want it. */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError<ApiProblem>(error)) {
    const data = error.response?.data;

    // Field errors first — "Code is required" beats "Validation failed".
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (data?.detail) return data.detail;
    if (data?.title) return data.title;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

/** The machine-readable code, for callers that branch on it rather than showing a message. */
export function errorCode(error: unknown): string | undefined {
  return axios.isAxiosError<ApiProblem>(error) ? error.response?.data?.code : undefined;
}

/** Field-level errors, shaped for react-hook-form's `setError`. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!axios.isAxiosError<ApiProblem>(error)) return {};
  const errors = error.response?.data?.errors;
  if (!errors) return {};

  return Object.fromEntries(
    Object.entries(errors)
      .map(([field, messages]) => [
        // The API sends PascalCase property names; forms use camelCase.
        field.charAt(0).toLowerCase() + field.slice(1),
        messages[0],
      ])
      .filter(([, message]) => Boolean(message)),
  );
}
